/**
 * icon-stacker - the stack itself: the registry, the DOM, the click road
 * and the corner-clearance variable the toasts read. The public surface is
 * `stacker` (exposed as g.iconStacker by index.js).
 */

const t = (text, vars) => (window.g ? window.g.translate(text, vars) : text);
const esc = (value) => (window.g ? window.g.esc(value) : String(value ?? ''));

const POSITIONS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
const LAYOUTS = ['vertical', 'horizontal'];

// The framework defaults - every value overridable per site
// (site.iconStacker), per page (route.php 'iconStacker') or at runtime
// (configure()). Sizes are CSS lengths; size + sizeHover share ONE unit
// (the grow ratio is computed from them).
const DEFAULTS = {
	position: 'bottom-right',
	layout: 'vertical',
	size: '2.5rem',        // the resting face
	sizeHover: '3.25rem',  // the face under the pointer / keyboard focus
	offset: '1.25rem',     // distance from both viewport edges
	gap: '0.625rem',       // between faces
	// proximity: the stack rests faded and comes up as the pointer nears it
	opacity: 0.1,          // at rest (the pointer farther than `reach`)
	opacityNear: 1,        // within `near` px of the stack (and on touch devices, always)
	reach: 'auto',         // px where the fade starts; auto = 300 at <=1440px wide … 400 at >=1920px
	near: 50,              // px from the stack where opacityNear is reached
};

const config = { ...DEFAULTS };
let icons = [];       // the registered list in order: first = nearest the corner
let hidden = false;   // hide()/show() + the page's `false`
let root = null;      // the stack element: created on first render, removed when empty
let counter = 0;
let pointer = null;   // the last known pointer position (null = outside the window)
let tracking = false; // the document listeners are attached
let frame = 0;        // the pending animation frame of the proximity update

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

function slug(name) {
	return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'icon-' + (++counter);
}

// A CSS length from a number (px) or a string; null when unusable.
function length(value) {
	if (typeof value === 'number' && Number.isFinite(value)) return value + 'px';
	if (typeof value === 'string' && /^\d*\.?\d+[a-z%]+$/i.test(value.trim())) return value.trim();
	return null;
}

// A number in [0, 1] (opacity) or null.
function fraction(value) {
	const n = typeof value === 'string' ? parseFloat(value) : value;
	return typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1 ? n : null;
}

// A pixel distance from a number or a 'Npx' string; null when unusable.
function pixels(value) {
	const n = typeof value === 'string' && /^\d*\.?\d+(px)?$/.test(value.trim()) ? parseFloat(value) : value;
	return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null;
}

// The effective reach in px: 'auto' scales with the viewport width
// (300 up to 1440px wide, 400 from 1920px, linear between).
function reach() {
	if (config.reach !== 'auto') return config.reach;
	const w = window.innerWidth;
	if (w <= 1440) return 300;
	if (w >= 1920) return 400;
	return Math.round(300 + ((w - 1440) / 480) * 100);
}

// Touch / coarse pointers have no "approach": the stack stays at opacityNear.
function coarse() {
	return !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

// The grow ratio = sizeHover / size (same unit); 1.3 when they disagree.
function ratio() {
	const unit = (v) => v.replace(/[\d.\s]/g, '');
	const a = parseFloat(config.size);
	const b = parseFloat(config.sizeHover);
	if (!a || !b || unit(config.size) !== unit(config.sizeHover)) return 1.3;
	return Math.round((b / a) * 1000) / 1000;
}

// One registered icon from a project definition; null (+ console error) when
// the minimum (a name + a face) is missing.
function normalize(def) {
	if (!isObject(def)) return null;
	const name = String(def.name ?? '').trim();
	if (!name) {
		console.error('[icon-stacker] an icon needs a name', def);
		return null;
	}
	if (!def.icon && !def.image) {
		console.error('[icon-stacker] an icon needs a face: icon (a ri-* class) or image (a png/webp/svg url)', def);
		return null;
	}
	return {
		id: String(def.id || slug(name)),
		name,
		icon: def.icon ? String(def.icon) : null,
		image: def.image ? String(def.image) : null,
		url: def.url ? String(def.url) : null,
		target: def.target ? String(def.target) : null,
		action: typeof def.action === 'function' ? def.action : null,
	};
}

function item(icon) {
	const label = t(icon.name);
	const face = icon.image
		? `<img class="icon-stacker-image" src="${esc(icon.image)}" alt="" />`
		: `<i class="${esc(icon.icon)}" aria-hidden="true"></i>`;
	const inner = `<span class="icon-stacker-face">${face}</span><span class="icon-stacker-label" aria-hidden="true">${esc(label)}</span>`;
	const common = `class="icon-stacker-item" data-icon-stacker="${esc(icon.id)}" aria-label="${esc(label)}"`;
	// a url without an action is a real link (middle-click, keyboard, new tab
	// all native); everything else is a button
	if (icon.url && !icon.action) {
		const target = icon.target ? ` target="${esc(icon.target)}" rel="noopener"` : '';
		return `<a ${common} href="${esc(icon.url)}"${target}>${inner}</a>`;
	}
	return `<button type="button" ${common}>${inner}</button>`;
}

// The click road: the document event first (cancelable - a listener may
// take over), then the icon's action or its url.
function onClick(e) {
	const el = e.target.closest('[data-icon-stacker]');
	if (!el) return;
	const icon = icons.find((i) => i.id === el.dataset.iconStacker);
	if (!icon) return;
	const event = new CustomEvent('icon-stacker:click', { bubbles: true, cancelable: true, detail: { id: icon.id, icon: { ...icon }, element: el } });
	if (!document.dispatchEvent(event)) {
		e.preventDefault();
		return;
	}
	if (icon.action) {
		e.preventDefault();
		icon.action(icon, e);
	}
}

// Proximity: the opacity from the pointer's distance to the stack's box —
// opacity at `reach` and beyond, opacityNear at `near` and closer, linear
// between. No pointer (outside the window) = at rest.
function proximity() {
	frame = 0;
	if (!root) return;
	let value = config.opacityNear;
	if (!coarse()) {
		value = config.opacity;
		if (pointer) {
			const r = root.getBoundingClientRect();
			const dx = Math.max(r.left - pointer.x, 0, pointer.x - r.right);
			const dy = Math.max(r.top - pointer.y, 0, pointer.y - r.bottom);
			const d = Math.hypot(dx, dy);
			const far = Math.max(reach(), config.near + 1);
			const t = d <= config.near ? 1 : d >= far ? 0 : (far - d) / (far - config.near);
			value = config.opacity + (config.opacityNear - config.opacity) * t;
		}
	}
	root.style.setProperty('--icon-stacker-opacity', String(Math.round(value * 1000) / 1000));
}

function schedule() {
	if (!frame) frame = requestAnimationFrame(proximity);
}

function onPointerMove(e) {
	pointer = { x: e.clientX, y: e.clientY };
	schedule();
}

function onPointerLeave(e) {
	// the pointer left the document (no related target) — rest
	if (e.relatedTarget === null) {
		pointer = null;
		schedule();
	}
}

function track() {
	if (tracking) return;
	tracking = true;
	document.addEventListener('pointermove', onPointerMove, { passive: true });
	document.documentElement.addEventListener('mouseleave', onPointerLeave);
	document.addEventListener('pointerleave', onPointerLeave);
}

// The corner the stack occupies is shared with the toast stack: publish the
// clearance (viewport edge -> the stack's far side + a gap) as a body
// variable the toast css reads (genus scss/_notify.scss).
function clearance() {
	const body = document.body;
	POSITIONS.forEach((p) => body.style.removeProperty('--genus-clear-' + p));
	if (!root) return;
	const rect = root.getBoundingClientRect();
	const gap = 8;
	const far = config.position.startsWith('bottom') ? window.innerHeight - rect.top : rect.bottom;
	body.style.setProperty('--genus-clear-' + config.position, Math.ceil(far + gap) + 'px');
}

function render() {
	if (hidden || !icons.length) {
		if (root) {
			root.remove();
			root = null;
		}
		clearance();
		return;
	}
	if (!root) {
		root = document.createElement('div');
		root.setAttribute('role', 'group');
		root.setAttribute('aria-label', t('Quick actions'));
		root.addEventListener('click', onClick);
		document.body.appendChild(root);
	}
	root.className = `icon-stacker icon-stacker-${config.position} icon-stacker-${config.layout}`;
	root.style.setProperty('--icon-stacker-size', config.size);
	root.style.setProperty('--icon-stacker-size-hover', config.sizeHover);
	root.style.setProperty('--icon-stacker-offset', config.offset);
	root.style.setProperty('--icon-stacker-gap', config.gap);
	root.style.setProperty('--icon-stacker-scale', String(ratio()));
	root.style.setProperty('--icon-stacker-opacity-near', String(config.opacityNear));
	root.style.setProperty('--icon-stacker-reach', reach() + 'px');
	root.innerHTML = icons.map(item).join('');
	clearance();
	track();
	proximity();
}

// Merge a settings block (position/layout/sizes) - unknown or invalid values
// are reported and ignored, the rest applies.
function configure(opts, paint = true) {
	if (isObject(opts)) {
		for (const key of Object.keys(opts)) {
			const value = opts[key];
			if (key === 'icons') continue;
			if (key === 'position') {
				if (POSITIONS.includes(value)) config.position = value;
				else console.error(`[icon-stacker] unknown position "${value}" - one of ${POSITIONS.join(', ')}`);
			} else if (key === 'layout') {
				if (LAYOUTS.includes(value)) config.layout = value;
				else console.error(`[icon-stacker] unknown layout "${value}" - one of ${LAYOUTS.join(', ')}`);
			} else if (key === 'opacity' || key === 'opacityNear') {
				const n = fraction(value);
				if (n !== null) config[key] = n;
				else console.error(`[icon-stacker] ${key} needs a number between 0 and 1, got`, value);
			} else if (key === 'reach' || key === 'near') {
				const n = key === 'reach' && value === 'auto' ? 'auto' : pixels(value);
				if (n !== null) config[key] = n;
				else console.error(`[icon-stacker] ${key} needs a distance in px${key === 'reach' ? " (or 'auto')" : ''}, got`, value);
			} else if (key in DEFAULTS) {
				const css = length(value);
				if (css) config[key] = css;
				else console.error(`[icon-stacker] ${key} needs a CSS length (a number = px), got`, value);
			} else if (key !== 'hidden') {
				console.error(`[icon-stacker] unknown setting "${key}"`);
			}
		}
		if (opts.hidden === true) hidden = true;
		if (opts.hidden === false) hidden = false;
	}
	if (paint) render();
	return { ...config };
}

// Upsert one or many definitions by id (an existing id keeps its place).
function add(defs, paint = true) {
	const list = Array.isArray(defs) ? defs : [defs];
	for (const def of list) {
		const icon = normalize(def);
		if (!icon) continue;
		const at = icons.findIndex((i) => i.id === icon.id);
		if (at === -1) icons.push(icon);
		else icons[at] = icon;
	}
	if (paint) render();
}

function set(defs, paint = true) {
	icons = [];
	add(Array.isArray(defs) ? defs : [], false);
	if (paint) render();
}

function remove(id) {
	icons = icons.filter((i) => i.id !== id);
	render();
}

// A settings block from the server (site config or route.php): its
// non-icon keys configure, its `icons` list REPLACES the current list.
function apply(block) {
	if (block === false) {
		hidden = true;
		return;
	}
	if (!isObject(block)) return;
	configure(block, false);
	if (Array.isArray(block.icons)) set(block.icons, false);
}

export const stacker = {
	configure,
	config: () => ({ ...config }),
	add: (defs) => add(defs),
	set: (defs) => set(defs),
	remove,
	clear: () => set([]),
	icons: () => icons.map((i) => ({ ...i })),
	hide() { hidden = true; render(); },
	show() { hidden = false; render(); },
	element: () => root,
	refresh: render,
};

// The server's two blocks (g.init hands them over as settings.iconStacker:
// { site, page }): the site's list on every page, the page's block over it.
export function boot() {
	const s = (window.g && window.g.settings.iconStacker) || {};
	apply(s.site);
	apply(s.page);
	render();
	window.addEventListener('resize', () => {
		clearance();
		if (root) root.style.setProperty('--icon-stacker-reach', reach() + 'px');
		schedule();
	});
}
